import { _, helpers, mui, React, ts, ui } from '_core';

import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  returns: string;
  fullScreen: boolean;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, returns, fullScreen, title }: ChartProps) => {
  const [tab, setTab] = React.useState<'return' | 'risk' | 'adjusted'>('return');
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: returns,
          query: ['tab', 'equals', tab],
        },
      });

      if (response) {
        setData(_.map(response.data, (el) => _.omit(el, 'tab')));
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (returns) loadData();
    return () => setData(null);
  }, [returns, tab]);

  const getTabs = () => {
    return (
      <mui.core.Tabs
        value={tab}
        indicatorColor="primary"
        textColor="primary"
        onChange={(e, newTab) => setTab(newTab)}
        aria-label="Return Horizon Tabs"
      >
        <mui.core.Tab label="Returns" value="return" />
        <mui.core.Tab label="Risk" value="risk" />
        <mui.core.Tab label="Risk Adjusted Returns" value="adjusted" />
      </mui.core.Tabs>
    );
  };

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    return nestedBarChartFormat(data);
  }, [data]);

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>
        <ui.NestedBarChart
          id={id}
          data={formattedData?.data}
          count={formattedData?.count}
          category="category"
          seriesOrder={(series) => _.sortBy(series.map((s) => Number(s) || 'category')).map((s) => s.toString())}
          numberFormat={tab !== 'adjusted' ? '#.##%' : '#.##'}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          fullScreen={fullScreen}
          exportTitle={title}
          readOnly={readOnly}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
